import React, { ReactNode, Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import Form, { Item } from './components/Form';
import { Button, Col, Row } from 'tea-component';
import { useForm } from 'react-final-form-hooks';
import { useDeepCompareEffect, useLatest, useUpdateEffect } from 'ahooks';
import './index.scss';
import classNames from 'classnames';
import { isArray, isEmpty, isFunction, isObject, pickBy } from 'lodash-es';
/**
 config描述
  name: from 表单的name
  component: 任意表单组件
  label: 表单的label值，可定制化成组件
  rules: 表单规则
 ISearchComponent描述
  column: 当前一列几个
  getFormValue: 提交回调
  btnOkText: 确认按钮的文字
  btnCancelText: 取消按钮的文字
  labelSpan: 全局自定义表单的label宽度， 缺省值：5
  initialValues: 根据表单config下name值，提供默认初始值
  resetFields: 一旦发生改变则立即重置成undefined,适用于多组件的联动选择重置,设置成组件状态,提供给此组件，在由表单事件去消费
  resetCallback: 重置回调
  isUnpack: 是否有展开收起 (false 为没有, 默认没有)
  isReset: 是否有重置按钮 (false 为没有,默认有)
  isCenter: 底部按钮是否居中(false 为没有,默认有)
  reset: 如提供翻页页码,模态框字段属性直接重置表单,适用于多组件的联动选择重置,设置成组件状态,提供给此组件，在由表单事件去消费
  ref: 表单Ref,
  extraBtn: 底部按钮其他元素(false 为没有,默认没有),
  bottomButtonStyle: 底部按钮className,
  isDisabledSearchBtn:是否开启表单如果全为空值，需要禁用btn，有一个值的情况，就放开
  isShowSearchBtn:是否开启表单底部按钮，如果关闭，需要主动通过ref来获取子组件submit方法，触发getFormValue方法,
  isOpenHistoryInput:是否开启输入框历史记录,
  className: 表单容器样式
 */

/**
 * @description: 模版
  const config=
 [
    {
      name:'对应字段',
      label:'搜索label',
      component:<Input placeholder='搜索提示'/>,
      rules:{message:'表单必填' ,required:true,validator:()=>{}},
    }
 ],
  const getFormValue=<V,>(value:V):void=>{
  },

  const SearchComponent=UseFormSearch({
    config,
    getFormValue: value => getFormValue(value),
    labelSpan: 6,
    isClose: false,
    initialValues:{'对应字段':'xx'}
  }),
 */
export interface IConfig<T = { message: string; required: boolean; validator?: (...args: any[]) => void }> {
  name: string;
  label: string | ReactNode;
  component: ReactNode;
  rules?: T;
}
// 如果使用外部btn，需要主动获取子组件提交方法
export interface ISearchRef<T = (...args: any[]) => void> {
  handleSubmit: T | undefined;
  onclose: T | undefined;
  restFormProps: { [key: string]: any };
  form: { [key: string]: any };
}

export interface ISearchComponent<B = boolean, S = string> {
  column?: number;
  config: IConfig[];
  getFormValue: <T>(value: T) => void;
  btnOkText?: S;
  btnCancelText?: S;
  labelSpan: number;
  resetFields?: S;
  initialValues?: { [key in IConfig['name']]: unknown };
  resetCallback?: () => void;
  isUnpack?: B;
  isReset?: B;
  isCenter?: B;
  reset?: unknown;
  ref?: ISearchRef | Ref<unknown> | undefined;
  extraBtn?: ReactNode;
  className?: S;
  isShowSearchBtn?: B;
  isDisabledSearchBtn?: B;
  isOpenHistoryInput?: B;
  bottomButtonStyle?: { [key in string]: string };
  [key: string]: any;
}
export const UseFormSearch = ({
  column = 4,
  config,
  getFormValue,
  btnOkText,
  btnCancelText,
  labelSpan,
  resetFields,
  initialValues,
  resetCallback,
  isUnpack = false,
  isReset = true,
  isCenter = true,
  reset = undefined,
  ref,
  extraBtn = undefined,
  bottomButtonStyle,
  className,
  isShowSearchBtn = true,
  isDisabledSearchBtn = false,
  isOpenHistoryInput = false,
  btnLoading = false,
  ...restProps
}: ISearchComponent) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [expand, setExpand] = useState(isUnpack);
  const { form, handleSubmit, ...restFormProps } = useForm({
    onSubmit: getFormValue,
    initialValues,
    validate: (props) => transformArrayToObj(config, props),
  });
  const latestCountRef = useLatest(restFormProps);
  // 每一项组件依次循环,设置为组件节点，属性透传给对应的每一项
  const getFields = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const span = 24 / column;
    // 如果column列少于配置项长度，就直接渲染默认column项，反之渲染配置项每一项长度
    // eslint-disable-next-line no-nested-ternary
    const count = !expand ? config.length : column < config.length ? column : config.length;
    const result: ReactNode[] = [];
    for (let i = 0; i < count; i += 1) {
      if (config[i]) {
        const { name, label, component, ...restProps }: { [key: string]: any } = config[i];
        const prop: any = {};
        if (component.type.name !== 'Input' && isOpenHistoryInput) {
          prop.getPopupContainer = () => document.getElementById('searchComponent');
        }
        // TODO:自适应每一列
        result.push(
          <Item
            {...{
              name,
              label,
              labelCol: { span: labelSpan ?? 25 },
              required: config[i]?.rules?.required,
              message: config[i]?.rules?.message,
              showStatusIcon: false,
              ...restProps,
            }}
          >
            <component.type {...component.props} key={component.key} ref={component.ref} {...prop} />
          </Item>,
        );
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column, config, expand, labelSpan]);
  // 配置修改，直接重置表单
  useUpdateEffect(() => {
    form.reset();
  }, [form, reset]);

  // 多级联动，直接重置传入的表单字段
  useEffect(() => {
    if (resetFields) {
      form.change(resetFields, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetFields]);

  // 展开收起，直接重新渲染
  useDeepCompareEffect(() => {
    getFields();
  }, [expand, getFields]);

  // 全部清空
  const onclose = () => {
    resetCallback?.();
    // 重置是否需要调一遍接口，目前来看不需要
    // getFormValue(initialValues);
    form.reset();
  };
  // 暴露给父组件的方法或属性
  useImperativeHandle(ref, () => ({
    handleSubmit,
    onclose,
    restFormProps,
    latestCountRef,
    form,
  }));

  return (
    <div id="searchComponent" className={classNames('dbl-search-container', className)}>
      <Form form={form} ref={ref} {...restProps}>
        {getFields?.()}
      </Form>
      <div className="dbl-search-container-btn">
        {isShowSearchBtn && (
          <Row gap={24}>
            <Col
              span={24}
              style={{
                textAlign: isCenter ? 'center' : 'left',
                ...bottomButtonStyle,
              }}
            >
              <Button
                type={'primary'}
                htmlType={'submit'}
                onClick={handleSubmit}
                loading={btnLoading}
                disabled={isDisabledSearchBtn && isObjectEmpty(restFormProps.values)}
              >
                {btnOkText ?? 'Confirm'}
              </Button>
              {isReset && (
                <Button disabled={btnLoading} onClick={onclose}>
                  {' '}
                  {btnCancelText ?? 'Reset'}
                </Button>
              )}
              {extraBtn}
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

const transformArrayToObj = (array: any, key: any) => {
  if (!isArray(array) || isEmpty(array)) return {};
  const obj: { [key in string]: unknown } = {};
  array.forEach((item) => {
    const { name, rules } = item;
    const { validator, required, message } = rules || {};
    // 自定义规则，自己返回
    if (validator && isFunction(validator)) {
      obj[name] = validator?.(key);
      return;
    }
    // 如果没有定义自定义规则，并且是必填项
    if (!validator && required) {
      // obj[name] = key[name] ? '' : message;
      let val = key[name];
      if (Array.isArray(val)) {
        obj[name] = val.length > 0 ? '' : message;
      } else {
        if (typeof val === 'string') {
          val = val.trim();
        }
        obj[name] = val ? '' : message;
      }
      return;
    }
  });
  return pickBy(obj, (value) => value);
};

function isObjectEmpty(obj: any) {
  if (!isObject(obj)) return false;
  return Object.values(obj).every((value) => {
    if (Array.isArray(value)) {
      return value.every((item) => !item);
    }
    return !value;
  });
}
